import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import type { Customer } from '../../types/customers.types';
import { Button, Card, CardContent, CardTitle } from '@shared/components';
import { PermissionGuard, usePermissions } from '@/features/auth';
import { EditCustomerForm } from '../EditCustomerForm/EditCustomerForm';
import { XIcon, PencilIcon } from 'lucide-react';

const CARD_CLASSES = 'flex flex-col';
const RESULT_CARD_CLASSES = 'hover:bg-mp-background';
const SELECTED_CARD_CLASSES = 'border-mp-primary ring-1 ring-mp-primary';
const BODY_CLASSES = 'flex flex-auto flex-col';
const LINE_CLASSES = 'grow text-sm';

interface CustomerCardProps {
    customer: Customer;
    className?: string;
    onSelectCustomer: (customer: Customer) => void;
    mode?: 'search-result' | 'selected';
    onClearSelection?: () => void;
    onCustomerUpdated?: (customer: Customer) => void;
    /**
     * Passed straight through to EditCustomerForm. See EditCustomerForm's
     * onSavePending doc — the customers feature never imports quote code.
     */
    onSavePending?: (updated: Customer) => Promise<void>;
}

export const CustomerCard: React.FC<CustomerCardProps> = ({
    customer,
    className = '',
    onSelectCustomer,
    mode = 'search-result',
    onClearSelection,
    onCustomerUpdated,
    onSavePending,
}) => {
    const { canEdit } = usePermissions();
    const canEditCustomer = canEdit('customer');
    const [isEditing, setIsEditing] = useState(false);

    // Mid-edit permission loss — revert to read-only. Mirrors PR #348 pattern.
    useEffect(() => {
        if (!canEditCustomer && isEditing) {
            setIsEditing(false);
        }
    }, [canEditCustomer, isEditing]);

    if (isEditing) {
        return (
            <Card className={clsx(CARD_CLASSES, className)}>
                <CardContent className={BODY_CLASSES}>
                    <EditCustomerForm
                        customer={customer}
                        onCancel={() => setIsEditing(false)}
                        onSaved={(updated) => {
                            onCustomerUpdated?.(updated);
                            setIsEditing(false);
                        }}
                        onSavePending={onSavePending}
                    />
                </CardContent>
            </Card>
        );
    }

    const cardClass = clsx(
        CARD_CLASSES,
        RESULT_CARD_CLASSES,
        mode === 'selected' && SELECTED_CARD_CLASSES,
        className
    );

    return (
        <Card className={cardClass}>
            <CardContent className={BODY_CLASSES}>
                <CardTitle>
                    {customer.title} {customer.forename} {customer.surname}
                </CardTitle>
                <p className={LINE_CLASSES}>
                    {customer.address_line_1}
                    {customer.address_line_2 && `, ${customer.address_line_2}`}
                </p>
                <p className={LINE_CLASSES}>
                    {customer.city}, {customer.postcode}
                </p>
                <p className={LINE_CLASSES}>{customer.email}</p>
                {customer.mobile_phone && (
                    <p className={LINE_CLASSES}>Mobile: {customer.mobile_phone}</p>
                )}
                {customer.business_name && (
                    <p className={LINE_CLASSES}>Business: {customer.business_name}</p>
                )}
                {customer.vat_number && <p className={LINE_CLASSES}>VAT: {customer.vat_number}</p>}

                <div className="flex flex-wrap items-center justify-end gap-2">
                    {mode === 'selected' && (
                        <PermissionGuard permission={{ resource: 'customer', action: 'edit' }}>
                            <Button
                                size="xs"
                                variant="clear"
                                onClick={() => setIsEditing(true)}
                                startIcon={<PencilIcon size={14} />}
                            >
                                Edit
                            </Button>
                        </PermissionGuard>
                    )}

                    {mode === 'search-result' && (
                        <Button size="xs" onClick={() => onSelectCustomer(customer)}>
                            Select
                        </Button>
                    )}

                    {mode === 'selected' && onClearSelection && (
                        <Button
                            size="xs"
                            variant="clear"
                            onClick={onClearSelection}
                            startIcon={<XIcon size={14} />}
                            ariaLabel="Clear selected customer"
                        />
                    )}
                </div>
            </CardContent>
        </Card>
    );
};
